import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

const PdfExport = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBoxes, setSelectedBoxes] = useState({});
  const [textAlign, setTextAlign] = useState('center');
  const [timePosition, setTimePosition] = useState('right');
  const [layoutFormat, setLayoutFormat] = useState('list');
  const [tableBorderStyle, setTableBorderStyle] = useState('classic');
  const [listStyle, setListStyle] = useState('plain');
  const [decorativeStyle, setDecorativeStyle] = useState('none');
  const [blackAndWhite] = useState(true); // Always black and white
  const [orientation, setOrientation] = useState('portrait');
  const [fitOnePage, setFitOnePage] = useState(false);
  const [includeLogo, setIncludeLogo] = useState(true); // Default to true if shul has a logo
  const [includeHeaderText, setIncludeHeaderText] = useState(false);
  const [headerText, setHeaderText] = useState('');
  const [headerTextSize, setHeaderTextSize] = useState(36);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Use ref to prevent race conditions
  const isGeneratingRef = React.useRef(false);
  const printWindowRef = React.useRef(null);
  const timeoutRef = React.useRef(null);

  // Fetch display data and box names
  const [displayData, setDisplayData] = useState(null);
  const [boxNames, setBoxNames] = useState({});

  useEffect(() => {
    if (isModalOpen && !displayData) {
      fetchDisplayData();
    }
  }, [isModalOpen, displayData]);

  // Separate effect for cleanup when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Close print window if it exists
      if (printWindowRef.current && !printWindowRef.current.closed) {
        try {
          printWindowRef.current.close();
        } catch (error) {
          // Silently fail
        }
        printWindowRef.current = null;
      }

      // Reset generating state
      if (isGeneratingRef.current) {
        isGeneratingRef.current = false;
        setIsGenerating(false);
      }

      // Clear header text when modal closes
      setHeaderText('');
    }
  }, [isModalOpen]);

  const fetchDisplayData = async () => {
    if (isLoading) return; // Prevent duplicate requests

    setIsLoading(true);
    try {
      // Get shul slug first
      const shulData = await api.get('/shul/');
      if (!shulData.slug) {
        console.error('No shul slug found');
        setIsLoading(false);
        return;
      }

      // Fetch display data
      const data = await api.get(`/display/${shulData.slug}/`);
      setDisplayData(data);

      // Set box names from V3 layout
      const layout = data.layout_config || data.layout;
      if (layout?.version === 'v2' && layout.boxes) {
        const names = {};
        layout.boxes.forEach((box) => {
          if (!box.isMemorialBox) {
            names[box.id] = box.displayName;
          }
        });
        setBoxNames(names);
      } else {
        // Fallback to old V1 layout structure
        if (data.layout) {
          setBoxNames({
            box1: data.layout.box1?.displayName || 'Box 1',
            box2: data.layout.box2?.displayName || 'Box 2',
            box3: data.layout.box3?.displayName || 'Box 3',
            box4: data.layout.box4?.displayName || 'Box 4',
            box5: data.layout.box5?.displayName || 'Box 5'
          });
        }
      }
    } catch (error) {
      console.error('Error fetching display data:', error);
      alert('Error loading display data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleBox = (boxId) => {
    setSelectedBoxes(prev => ({ ...prev, [boxId]: !prev[boxId] }));
  };

  const selectAll = () => {
    const allBoxes = {};
    Object.keys(boxNames).forEach(boxId => {
      allBoxes[boxId] = true;
    });
    setSelectedBoxes(allBoxes);
  };

  const deselectAll = () => {
    const allBoxes = {};
    Object.keys(boxNames).forEach(boxId => {
      allBoxes[boxId] = false;
    });
    setSelectedBoxes(allBoxes);
  };

  // Helper to format times for display
  const formatTimeForDisplay = (time24h, use12Hour, showAmPm = false) => {
    if (!time24h || !use12Hour) return time24h;

    const parts = time24h.split(':');
    if (parts.length < 2) return time24h;

    let hour = parseInt(parts[0], 10);
    const minute = parts[1];
    const seconds = parts[2] || '';

    if (isNaN(hour)) return time24h;

    const ampm = showAmPm ? (hour >= 12 ? ' PM' : ' AM') : '';

    if (hour === 0) {
      hour = 12;
    } else if (hour > 12) {
      hour = hour - 12;
    }

    const timeStr = seconds ? `${hour}:${minute}:${seconds}` : `${hour}:${minute}`;
    return `${timeStr}${ampm}`;
  };

  // Helper to get item value
  const getItemValue = (item) => {
    if (!item || !item.id) return null;

    const itemId = item.id;

    // Handle different data sources
    if (itemId.startsWith('zmanim_tomorrow_')) {
      const field = itemId.replace('zmanim_tomorrow_', '');
      return displayData.zmanim_tomorrow?.[field];
    } else if (itemId.startsWith('zmanim_')) {
      const field = itemId.replace('zmanim_', '');
      return displayData.zmanim?.[field];
    } else if (itemId.startsWith('limudim_tomorrow_')) {
      const field = itemId.replace('limudim_tomorrow_', '');
      return displayData.limudim_tomorrow?.[field];
    } else if (itemId.startsWith('limudim_')) {
      const field = itemId.replace('limudim_', '');
      return displayData.limudim?.[field];
    } else if (itemId.startsWith('calendar_tomorrow_')) {
      const field = itemId.replace('calendar_tomorrow_', '');
      return displayData.jewish_calendar_tomorrow?.[field];
    } else if (itemId.startsWith('calendar_')) {
      const field = itemId.replace('calendar_', '');
      return displayData.jewish_calendar?.[field];
    } else if (itemId.startsWith('customtext_')) {
      const internalName = itemId.replace('customtext_', '');
      const customText = displayData.custom_texts?.find(ct => ct.internal_name === internalName);
      if (customText) {
        return { _customText: customText };
      }
    } else {
      // Try custom times
      const customTime = displayData.custom_times?.find(ct => ct.internal_name === itemId);
      if (customTime) {
        return customTime.time;
      }
      const customTimeTomorrow = displayData.custom_times_tomorrow?.find(ct => ct.internal_name === itemId);
      if (customTimeTomorrow) {
        return customTimeTomorrow.time;
      }
    }

    return null;
  };

  // Helper to get display name
  const getDisplayName = (item) => {
    if (!item || !item.name) return item?.name || '';

    // Use the provided name as the base
    const baseName = item.name;

    // Try to find translated name using the base name
    const zmanimDisplayNames = displayData?.zmanim_display_names || {};
    const limudimDisplayNames = displayData?.limudim_display_names || {};
    const calendarDisplayNames = displayData?.calendar_display_names || {};

    let translatedName = baseName;

    if (zmanimDisplayNames[baseName]) {
      translatedName = zmanimDisplayNames[baseName];
    } else if (limudimDisplayNames[baseName]) {
      translatedName = limudimDisplayNames[baseName];
    } else if (calendarDisplayNames[baseName]) {
      translatedName = calendarDisplayNames[baseName];
    }

    return translatedName;
  };

  // Format value
  const formatValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  // Map box items
  const getBoxItems = (boxId) => {
    // Get the layout (could be V2 or V1)
    const layout = displayData?.layout_config || displayData?.layout;

    let boxData;
    if (layout?.version === 'v2' && layout.boxes) {
      // V3 layout structure
      boxData = layout.boxes.find(b => b.id === boxId);
    } else {
      // V1 layout structure (fallback)
      boxData = displayData?.layout?.[boxId];
    }

    if (!boxData || !boxData.items) return [];

    return boxData.items.map(item => {
      const value = getItemValue(item);

      // Handle custom text
      if (value && value._customText) {
        const ct = value._customText;
        if (ct.text_type === 'divider') {
          return { isDivider: true, dividerColor: ct.font_color };
        }
        if (ct.text_type === 'line_space') {
          return { isLineSpace: true, lineThickness: ct.line_thickness || 40 };
        }
        return {
          isCustomText: true,
          label: ct.display_name,
          text: ct.text_content,
          fontSize: ct.font_size,
          fontColor: ct.font_color,
          textAlign: ct.text_align
        };
      }

      const formatted = formatValue(value);
      const isTomorrow = item.id && item.id.includes('tomorrow');

      return formatted ? {
        time: formatted,
        label: getDisplayName(item),
        isTomorrow
      } : null;
    }).filter(Boolean);
  };

  const generatePdf = async () => {
    // Prevent multiple simultaneous generations
    if (isGeneratingRef.current) {
      return;
    }

    if (!displayData) {
      alert('Display data not loaded. Please try again.');
      return;
    }

    const selected = Object.keys(selectedBoxes).filter(key => selectedBoxes[key]);
    if (selected.length === 0) {
      alert('Please select at least one box to export.');
      return;
    }

    isGeneratingRef.current = true;
    setIsGenerating(true);

    // Use setTimeout to ensure UI updates before heavy processing
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      try {
        generatePdfContent();
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('An error occurred while generating the PDF. Please try again.');
        isGeneratingRef.current = false;
        setIsGenerating(false);
      }
    }, 100);
  };

  const generatePdfContent = () => {
    // Abort if modal was closed
    if (!isModalOpen) {
      isGeneratingRef.current = false;
      setIsGenerating(false);
      return;
    }

    let hasReset = false;

    const resetGeneratingState = () => {
      if (!hasReset) {
        hasReset = true;
        isGeneratingRef.current = false;
        setIsGenerating(false);
      }
    };

    try {
      const selected = Object.keys(selectedBoxes).filter(key => selectedBoxes[key]);

    // Get shul styling and logo
    const shul = displayData.shul || {};
    const timeFormat = shul.time_format || '24h';
    const showAmPm = shul.show_am_pm || false;
    const language = shul.language || 'en';
    const isHebrewLanguage = language === 'he' || language === 'sh' || language === 'ah';
    const shulLogoUrl = displayData.shul?.center_logo || null;

    // Calculate adjustments for fit-on-one-page mode
    const numBoxes = selected.length;
    let useTwoColumns = false;
    let fontSizeMultiplier = 1;

    if (fitOnePage && numBoxes > 0) {
      // Always use 2 columns for fit-on-one-page mode
      useTwoColumns = true;

      // Reduce font size based on number of boxes - more aggressive
      if (numBoxes >= 5) {
        fontSizeMultiplier = 0.5; // 50% of original size
      } else if (numBoxes >= 4) {
        fontSizeMultiplier = 0.6; // 60% of original size
      } else if (numBoxes >= 3) {
        fontSizeMultiplier = 0.7; // 70% of original size
      } else if (numBoxes >= 2) {
        fontSizeMultiplier = 0.8; // 80% of original size
      }

    }

    // Create a new window for printing
    let printWindow;
    try {
      printWindow = window.open('', '', 'width=800,height=600');
      printWindowRef.current = printWindow;
    } catch (error) {
      console.error('Error opening print window:', error);
      alert('Error opening print window. Please check your browser settings.');
      resetGeneratingState();
      return;
    }

    if (!printWindow) {
      alert('Please allow popups for this site to generate PDFs.');
      resetGeneratingState();
      return;
    }

    // Simplify data for performance
    const selectedBoxIds = selected.slice(0, 5); // Limit to 5 boxes max to prevent freeze

    // Build HTML content using array for better performance
    const htmlParts = [];
    htmlParts.push(`
      <!DOCTYPE html>
      <html dir="${isHebrewLanguage ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8">
        <title></title>
        <style>
          @page {
            size: ${orientation};
            margin: 0.5in;
          }

          html, body {
            margin: 0;
            padding: 0;
          }

          body {
            font-family: ${shul.box1_text_font || 'Arial'}, sans-serif;
            margin: 0;
            padding: 20px;
            ${blackAndWhite ? 'color: #000000;' : ''}
          }

          .header-container {
            text-align: center;
            margin-bottom: 30px;
          }

          .header-container img {
            max-width: 100%;
            max-height: 150px;
            object-fit: contain;
            margin-bottom: 2px;
          }

          .header-text {
            font-size: ${headerTextSize}px;
            font-weight: 600;
            color: #000000;
            margin: 0;
          }

          .footer-credit {
            position: fixed;
            bottom: 0.5in;
            right: 0.5in;
            font-size: 10px;
            color: #666;
          }

          @media print {
            .footer-credit {
              position: fixed;
              bottom: 0.3in;
              right: 0.5in;
            }
          }

          .boxes-wrapper {
            width: 100%;
          }

          .box-container {
            page-break-after: always;
            margin-bottom: 40px;
            ${decorativeStyle === 'ornate' ? `
              border: 3px solid ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
              border-radius: 20px;
              padding: 25px;
              position: relative;
              box-shadow: inset 0 0 0 5px white, inset 0 0 0 8px ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
            ` : ''}
            ${decorativeStyle === 'curved' ? `
              border: 2px solid ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
              border-radius: 30px;
              padding: 30px;
              background: linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(250,250,250,1) 100%);
            ` : ''}
            ${decorativeStyle === 'waves' ? `
              border: 3px dashed ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
              border-radius: 15px;
              padding: 25px;
              position: relative;
            ` : ''}
          }

          .box-container:last-child {
            page-break-after: auto;
          }

          /* Ornate corner decorations */
          ${decorativeStyle === 'ornate' ? `
            .box-container::before {
              content: '✦';
              position: absolute;
              top: -10px;
              left: 50%;
              transform: translateX(-50%);
              background: white;
              padding: 0 15px;
              font-size: 18px;
              color: ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
            }
            .box-container::after {
              content: '✦';
              position: absolute;
              bottom: -10px;
              left: 50%;
              transform: translateX(-50%);
              background: white;
              padding: 0 15px;
              font-size: 18px;
              color: ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
            }
          ` : ''}

          /* Wave pattern corners */
          ${decorativeStyle === 'waves' ? `
            .box-container::before {
              content: '∿';
              position: absolute;
              top: -2px;
              left: 10px;
              background: white;
              padding: 0 10px;
              font-size: 24px;
              color: ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
            }
            .box-container::after {
              content: '∿';
              position: absolute;
              top: -2px;
              right: 10px;
              background: white;
              padding: 0 10px;
              font-size: 24px;
              color: ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
            }
          ` : ''}

          .box-title {
            font-size: 28px;
            font-weight: 600;
            margin-bottom: 30px;
            text-align: ${textAlign};
            padding-bottom: 15px;
            border-bottom: 2px solid ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
          }

          .box-items {
            text-align: ${textAlign};
            ${fitOnePage && useTwoColumns && layoutFormat === 'list' ? `
              column-count: 2;
              column-gap: 30px;
            ` : ''}
            ${fitOnePage && useTwoColumns && layoutFormat === 'table' ? `
              display: flex;
              gap: 15px;
            ` : ''}
          }

          ${fitOnePage && useTwoColumns && layoutFormat === 'table' ? `
            .times-table {
              flex: 1;
              width: auto;
            }
          ` : ''}

          /* List format styles */
          .item {
            margin-bottom: ${listStyle === 'plain' ? '15px' : '0'};
            font-size: 18px;
            line-height: 1.6;
            text-align: ${textAlign};
            ${listStyle === 'boxed' ? `
              background-color: ${blackAndWhite ? '#f9f9f9' : 'rgba(212, 175, 55, 0.08)'};
              border: 1px solid ${blackAndWhite ? '#cccccc' : (shul.boxes_outline_color || '#d4af37')};
              border-radius: 8px;
              padding: 12px 18px;
              margin-bottom: 10px;
            ` : ''}
            ${listStyle === 'underlined' ? `
              border-bottom: 2px solid ${blackAndWhite ? '#cccccc' : (shul.boxes_outline_color || '#d4af37')};
              padding-bottom: 12px;
              padding-top: 12px;
              margin-bottom: 0;
            ` : ''}
            ${listStyle === 'shadowed' ? `
              background-color: ${blackAndWhite ? '#ffffff' : '#ffffff'};
              border: 1px solid ${blackAndWhite ? '#dddddd' : '#e0e0e0'};
              border-radius: 10px;
              padding: 14px 20px;
              margin-bottom: 12px;
              box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
            ` : ''}
          }

          ${listStyle === 'underlined' ? `
            .item:first-child {
              padding-top: 0;
            }
            .item:last-child {
              border-bottom: none;
              padding-bottom: 0;
            }
          ` : ''}

          .item-label {
            display: inline;
            font-weight: 400;
          }

          .item-time {
            display: inline;
            font-weight: 500;
            ${timePosition === 'left' ? 'margin-right: 10px;' : 'margin-left: 10px;'}
          }

          /* Table format styles */
          .times-table {
            width: 100%;
            border-collapse: ${tableBorderStyle === 'minimal' ? 'separate' : 'collapse'};
            ${tableBorderStyle === 'minimal' ? 'border-spacing: 0;' : ''}
            margin-top: 10px;
          }

          /* Classic Border Style */
          ${tableBorderStyle === 'classic' ? `
            .times-table td {
              padding: 10px 15px;
              border: 1px solid ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
              text-align: ${textAlign};
              font-size: 18px;
            }
          ` : ''}

          /* Double Border Style */
          ${tableBorderStyle === 'double' ? `
            .times-table {
              border: 3px double ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
            }
            .times-table td {
              padding: 12px 18px;
              border: 3px double ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
              text-align: ${textAlign};
              font-size: 18px;
            }
          ` : ''}

          /* Minimal/Elegant Border Style */
          ${tableBorderStyle === 'minimal' ? `
            .times-table {
              border-top: 2px solid ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
              border-bottom: 2px solid ${blackAndWhite ? '#000000' : (shul.boxes_outline_color || '#d4af37')};
            }
            .times-table td {
              padding: 12px 15px;
              border-bottom: 1px solid ${blackAndWhite ? '#cccccc' : '#e0e0e0'};
              text-align: ${textAlign};
              font-size: 18px;
            }
            .times-table tr:last-child td {
              border-bottom: none;
            }
          ` : ''}

          .times-table .time-col {
            font-weight: 500;
            width: 30%;
          }

          .times-table .label-col {
            font-weight: 400;
            width: 70%;
          }

          .divider {
            border-top: 2px solid ${blackAndWhite ? '#000000' : '#ccc'};
            margin: 20px 0;
          }

          .line-space {
            height: 20px;
          }

          .custom-text {
            font-style: italic;
            margin: 10px 0;
          }

          @media print {
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        ${(includeLogo && shulLogoUrl) || (includeHeaderText && headerText) ? `
          <div class="header-container">
            ${includeLogo && shulLogoUrl ? `<img src="${shulLogoUrl}" alt="Logo" />` : ''}
            ${includeHeaderText && headerText ? `<div class="header-text">${headerText}</div>` : ''}
          </div>
        ` : ''}
        <div class="boxes-wrapper">
    `);

    // Add each selected box (use the limited list)
    selectedBoxIds.forEach((boxId, index) => {
      const boxTitle = boxNames[boxId] || boxId;
      const items = getBoxItems(boxId);

      // Get box-specific styling
      const titleFont = shul[`${boxId}_title_font`] || shul.box1_title_font || 'Arial';
      const titleColor = blackAndWhite ? '#000000' : (shul[`${boxId}_title_color`] || shul.box1_title_color || '#ffc764');
      const textFont = shul[`${boxId}_text_font`] || shul.box1_text_font || 'Arial';
      const textColor = blackAndWhite ? '#000000' : (shul[`${boxId}_text_color`] || shul.box1_text_color || '#ffc764');

      htmlParts.push(`
        <div class="box-container">
          <div class="box-title" style="font-family: ${titleFont}; color: ${titleColor};">
            ${boxTitle}
          </div>
          <div class="box-items" style="font-family: ${textFont}; color: ${textColor};">
      `);

      // Split items for 2-column table if needed
      const shouldSplitTable = layoutFormat === 'table' && fitOnePage && useTwoColumns;
      const midpoint = shouldSplitTable ? Math.ceil(items.length / 2) : items.length;
      const itemsFirstHalf = shouldSplitTable ? items.slice(0, midpoint) : items;
      const itemsSecondHalf = shouldSplitTable ? items.slice(midpoint) : [];

      // Function to render table rows
      const renderTableItems = (itemsArray) => {
        itemsArray.forEach(item => {
        if (item.isDivider) {
          if (layoutFormat === 'table') {
            // Skip dividers in table format or add a special row
            const dividerColor = blackAndWhite ? '#000000' : (item.dividerColor || '#ccc');
            htmlParts.push(`<tr><td colspan="2" style="padding: 0;"><div class="divider" style="border-color: ${dividerColor}; margin: 0;"></div></td></tr>`);
          } else {
            const dividerColor = blackAndWhite ? '#000000' : (item.dividerColor || '#ccc');
            htmlParts.push(`<div class="divider" style="border-color: ${dividerColor};"></div>`);
          }
        } else if (item.isLineSpace) {
          if (layoutFormat === 'table') {
            htmlParts.push(`<tr><td colspan="2" style="padding: 0; height: ${item.lineThickness || 20}px;"></td></tr>`);
          } else {
            htmlParts.push(`<div class="line-space" style="height: ${item.lineThickness || 20}px;"></div>`);
          }
        } else if (item.isCustomText) {
          const customTextColor = blackAndWhite ? '#000000' : (item.fontColor || textColor);
          const customTextAlign = item.textAlign || textAlign;
          if (layoutFormat === 'table') {
            htmlParts.push(`
              <tr>
                <td colspan="2" class="custom-text" style="color: ${customTextColor}; text-align: ${customTextAlign}; font-size: ${item.fontSize || 18}px;">
                  ${item.text}
                </td>
              </tr>
            `);
          } else {
            htmlParts.push(`
              <div class="custom-text" style="color: ${customTextColor}; text-align: ${customTextAlign}; font-size: ${item.fontSize || 18}px;">
                ${item.text}
              </div>
            `);
          }
        } else {
          const displayTime = formatTimeForDisplay(item.time, timeFormat === '12h', showAmPm);

          if (layoutFormat === 'table') {
            // Table format
            if (timePosition === 'left') {
              htmlParts.push(`
                <tr>
                  <td class="time-col">${displayTime}</td>
                  <td class="label-col">${item.label}</td>
                </tr>
              `);
            } else {
              htmlParts.push(`
                <tr>
                  <td class="label-col">${item.label}</td>
                  <td class="time-col">${displayTime}</td>
                </tr>
              `);
            }
          } else {
            // List format
            const shouldTimeBeFirst = (isHebrewLanguage && timePosition === 'right') ||
                                     (!isHebrewLanguage && timePosition === 'left');

            if (shouldTimeBeFirst) {
              htmlParts.push(`
                <div class="item">
                  <span class="item-time">${displayTime}</span>
                  <span class="item-label">${item.label}</span>
                </div>
              `);
            } else {
              htmlParts.push(`
                <div class="item">
                  <span class="item-label">${item.label}</span>
                  <span class="item-time">${displayTime}</span>
                </div>
              `);
            }
          }
        }
        });
      };

      // Start table if table format is selected
      if (layoutFormat === 'table') {
        if (shouldSplitTable) {
          // Two tables side by side
          htmlParts.push(`
            <table class="times-table">
              <tbody>
          `);
          renderTableItems(itemsFirstHalf);
          htmlParts.push(`
              </tbody>
            </table>
            <table class="times-table">
              <tbody>
          `);
          renderTableItems(itemsSecondHalf);
          htmlParts.push(`
              </tbody>
            </table>
          `);
        } else {
          // Single table
          htmlParts.push(`
            <table class="times-table">
              <tbody>
          `);
          renderTableItems(itemsFirstHalf);
          htmlParts.push(`
              </tbody>
            </table>
          `);
        }
      } else {
        // List format - render all items normally
        renderTableItems(itemsFirstHalf);
      }

      htmlParts.push(`
          </div>
        </div>
      `);
    });

    htmlParts.push(`
        </div>
        <div class="footer-credit">Generated by shulschedule.com</div>
      </body>
      </html>
    `);

    // Join all parts into final HTML
    const htmlContent = htmlParts.join('');

    // Write content in chunks to avoid freezing
    try {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    } catch (error) {
      console.error('Error writing to print window:', error);
      alert('Error generating PDF. Please try again.');
      printWindow.close();
      resetGeneratingState();
      return;
    }

    // Wait for content to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        try {
          printWindow.print();
        } catch (error) {
          console.error('Error printing:', error);
          alert('Error opening print dialog. Please try again.');
        } finally {
          resetGeneratingState();
        }
      }, 500);
    };

    // Fallback timeout in case onload doesn't fire
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      resetGeneratingState();
    }, 5000);

    } catch (error) {
      console.error('Critical error in generatePdfContent:', error);
      alert('An unexpected error occurred. Please try again.');
      resetGeneratingState();
    }
  };

  const styles = {
    button: {
      padding: '12px 24px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '0',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
    },
    modalHeader: {
      padding: '24px 30px',
      borderBottom: '1px solid #e1e8ed',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      backgroundColor: 'white',
      borderRadius: '16px 16px 0 0',
      zIndex: 1
    },
    modalTitle: {
      margin: 0,
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#2c3e50'
    },
    modalCloseButton: {
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '28px',
      color: '#95a5a6',
      cursor: 'pointer',
      padding: '0',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '6px',
      transition: 'all 0.2s ease'
    },
    modalBody: {
      padding: '30px'
    },
    section: {
      marginBottom: '30px'
    },
    sectionTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '15px'
    },
    checkboxGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      cursor: 'pointer',
      fontSize: '15px',
      color: '#374151',
      padding: '8px',
      borderRadius: '6px',
      transition: 'background-color 0.2s'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    radioGroup: {
      display: 'flex',
      gap: '15px',
      flexWrap: 'wrap'
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
      fontSize: '15px',
      color: '#374151'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      marginBottom: '15px'
    },
    smallButton: {
      padding: '6px 12px',
      fontSize: '13px',
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    actionButtons: {
      display: 'flex',
      gap: '12px',
      marginTop: '30px',
      paddingTop: '20px',
      borderTop: '1px solid #e1e8ed'
    },
    generateButton: {
      flex: 1,
      padding: '12px 24px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    cancelButton: {
      padding: '12px 24px',
      backgroundColor: '#e5e7eb',
      color: '#374151',
      border: 'none',
      borderRadius: '8px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        style={styles.button}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
      >
        <span style={{ fontSize: '18px' }}>📄</span>
        Export to PDF
      </button>

      {isModalOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Export to PDF</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                style={styles.modalCloseButton}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f7fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <div style={{ fontSize: '18px', marginBottom: '10px' }}>Loading display data...</div>
                  <div style={{ fontSize: '14px' }}>Please wait</div>
                </div>
              ) : (
                <>
                {(() => {
                  // Get shul logo URL for use in this scope
                  const shulLogoUrl = displayData?.shul?.center_logo || null;
                  return (
                    <>
              {/* Select Boxes */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Select Display Boxes</div>
                {Object.keys(boxNames).length > 0 ? (
                  <>
                    <div style={styles.buttonGroup}>
                      <button
                        onClick={selectAll}
                        style={styles.smallButton}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                      >
                        Select All
                      </button>
                      <button
                        onClick={deselectAll}
                        style={styles.smallButton}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                      >
                        Deselect All
                      </button>
                    </div>
                    <div style={styles.checkboxGroup}>
                      {Object.keys(boxNames).map(boxId => (
                        <label
                          key={boxId}
                          style={styles.checkboxLabel}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          <input
                            type="checkbox"
                            checked={selectedBoxes[boxId] || false}
                            onChange={() => toggleBox(boxId)}
                            style={styles.checkbox}
                          />
                          {boxNames[boxId] || boxId}
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#6b7280', fontSize: 14, fontStyle: 'italic' }}>
                    No boxes available for export
                  </div>
                )}
              </div>

              {/* Text Alignment */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Text Alignment</div>
                <div style={styles.radioGroup}>
                  {['left', 'center', 'right'].map(align => (
                    <label key={align} style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="textAlign"
                        value={align}
                        checked={textAlign === align}
                        onChange={(e) => setTextAlign(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      {align.charAt(0).toUpperCase() + align.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Layout Format */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Layout Format</div>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="layoutFormat"
                      value="list"
                      checked={layoutFormat === 'list'}
                      onChange={(e) => setLayoutFormat(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    List
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="layoutFormat"
                      value="table"
                      checked={layoutFormat === 'table'}
                      onChange={(e) => setLayoutFormat(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    Table/Grid
                  </label>
                </div>
              </div>

              {/* List Style (only show when list format is selected) */}
              {layoutFormat === 'list' && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>List Style</div>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="listStyle"
                        value="plain"
                        checked={listStyle === 'plain'}
                        onChange={(e) => setListStyle(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      Plain
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="listStyle"
                        value="boxed"
                        checked={listStyle === 'boxed'}
                        onChange={(e) => setListStyle(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      Boxed
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="listStyle"
                        value="underlined"
                        checked={listStyle === 'underlined'}
                        onChange={(e) => setListStyle(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      Underlined
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="listStyle"
                        value="shadowed"
                        checked={listStyle === 'shadowed'}
                        onChange={(e) => setListStyle(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      Shadowed
                    </label>
                  </div>
                </div>
              )}

              {/* Table Border Style (only show when table format is selected) */}
              {layoutFormat === 'table' && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>Table Border Style</div>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="tableBorderStyle"
                        value="classic"
                        checked={tableBorderStyle === 'classic'}
                        onChange={(e) => setTableBorderStyle(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      Classic Grid
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="tableBorderStyle"
                        value="double"
                        checked={tableBorderStyle === 'double'}
                        onChange={(e) => setTableBorderStyle(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      Double Line
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="tableBorderStyle"
                        value="minimal"
                        checked={tableBorderStyle === 'minimal'}
                        onChange={(e) => setTableBorderStyle(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      Minimal/Elegant
                    </label>
                  </div>
                </div>
              )}

              {/* Decorative Style */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Decorative Accents</div>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="decorativeStyle"
                      value="none"
                      checked={decorativeStyle === 'none'}
                      onChange={(e) => setDecorativeStyle(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    None
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="decorativeStyle"
                      value="ornate"
                      checked={decorativeStyle === 'ornate'}
                      onChange={(e) => setDecorativeStyle(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    Ornate ✦
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="decorativeStyle"
                      value="curved"
                      checked={decorativeStyle === 'curved'}
                      onChange={(e) => setDecorativeStyle(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    Curved Arc
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="decorativeStyle"
                      value="waves"
                      checked={decorativeStyle === 'waves'}
                      onChange={(e) => setDecorativeStyle(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    Wave Pattern
                  </label>
                </div>
              </div>

              {/* Time Position */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Time Position</div>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="timePosition"
                      value="left"
                      checked={timePosition === 'left'}
                      onChange={(e) => setTimePosition(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    Left of Label
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="timePosition"
                      value="right"
                      checked={timePosition === 'right'}
                      onChange={(e) => setTimePosition(e.target.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    Right of Label
                  </label>
                </div>
              </div>

              {/* Orientation */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Page Orientation</div>
                <div style={styles.radioGroup}>
                  {['portrait', 'landscape'].map(orient => (
                    <label key={orient} style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="orientation"
                        value={orient}
                        checked={orientation === orient}
                        onChange={(e) => setOrientation(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      {orient.charAt(0).toUpperCase() + orient.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Fit on One Page Toggle */}
              <div style={styles.section}>
                <label
                  style={{ ...styles.checkboxLabel, display: 'inline-flex', alignItems: 'center' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <input
                    type="checkbox"
                    checked={fitOnePage}
                    onChange={(e) => setFitOnePage(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Optimize for Single Page
                    <span
                      title="Automatically adjusts font size and uses 2-column layout to fit content on one page. Note: Very large content may still require multiple pages."
                      style={{
                        cursor: 'help',
                        fontSize: '14px',
                        color: '#6b7280',
                        fontWeight: '600'
                      }}
                    >
                      ⓘ
                    </span>
                  </span>
                </label>
              </div>

              {/* Header Section (Logo and/or Text) */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Header (Optional)</div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '1rem' }}>
                  Add your shul's logo and/or text to display at the top center of the PDF
                </p>

                {/* Include Logo Checkbox */}
                {shulLogoUrl && (
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={includeLogo}
                        onChange={(e) => setIncludeLogo(e.target.checked)}
                        style={styles.checkbox}
                      />
                      Include Shul Logo
                    </label>
                    {includeLogo && (
                      <div style={{
                        marginTop: '8px',
                        padding: '1rem',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <img
                          src={shulLogoUrl}
                          alt="Shul logo preview"
                          style={{
                            maxHeight: '100px',
                            maxWidth: '200px',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {!shulLogoUrl && (
                  <div style={{
                    marginBottom: '1rem',
                    padding: '1rem',
                    backgroundColor: '#fef3c7',
                    border: '1px solid #fcd34d',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#92400e'
                  }}>
                    <strong>No logo uploaded yet.</strong> You can upload a logo in the Shul Settings page.
                  </div>
                )}

                {/* Header Text */}
                <div>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={includeHeaderText}
                      onChange={(e) => setIncludeHeaderText(e.target.checked)}
                      style={styles.checkbox}
                    />
                    Include Header Text
                  </label>
                  {includeHeaderText && (
                    <div style={{ marginTop: '8px' }}>
                      <input
                        type="text"
                        value={headerText}
                        onChange={(e) => setHeaderText(e.target.value)}
                        placeholder="Enter header text (e.g., Congregation Beth Israel)"
                        style={{
                          ...styles.input,
                          fontSize: '14px',
                          marginBottom: '8px'
                        }}
                      />
                      <div>
                        <label style={styles.label}>Font Size: {headerTextSize}px</label>
                        <input
                          type="range"
                          min="16"
                          max="72"
                          value={headerTextSize}
                          onChange={(e) => setHeaderTextSize(parseInt(e.target.value))}
                          style={{ width: '100%', marginTop: '4px' }}
                        />
                        <div style={{
                          fontSize: '11px',
                          color: '#6b7280',
                          marginTop: '4px'
                        }}>
                          Adjust the font size (16px - 72px)
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Print Settings Note */}
              <div style={{
                marginTop: '20px',
                padding: '12px 16px',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#1e40af',
                lineHeight: '1.5'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '6px' }}>💡 Tip: Remove Headers & Footers</div>
                <div style={{ fontSize: '12px', color: '#3b82f6' }}>
                  For a cleaner print:
                  <br />
                  <strong>Chrome/Edge:</strong> Click "More settings" → Uncheck "Headers and footers"
                  <br />
                  <strong>Firefox:</strong> Page Setup → Set all Headers & Footers to "blank"
                  <br />
                  <strong>Safari:</strong> Uncheck "Print headers and footers"
                </div>
              </div>

              {/* Action Buttons */}
              <div style={styles.actionButtons}>
                <button
                  onClick={generatePdf}
                  disabled={isGenerating}
                  style={{
                    ...styles.generateButton,
                    ...(isGenerating ? { backgroundColor: '#9ca3af', cursor: 'not-allowed' } : {})
                  }}
                  onMouseEnter={(e) => !isGenerating && (e.target.style.backgroundColor = '#059669')}
                  onMouseLeave={(e) => !isGenerating && (e.target.style.backgroundColor = '#10b981')}
                >
                  {isGenerating ? 'Generating...' : 'Generate & Print'}
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  style={styles.cancelButton}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d1d5db'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                >
                  Cancel
                </button>
              </div>
                    </>
                  );
                })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PdfExport;
